<template>
  <VForm @submit.prevent="register">
    <VRow>
      <VCol cols="12" class="py-0">
        <div class="title title-register mb-md-8 mb-2 mt-5">
          <img src="../assets/img/rabota/int-company.svg" alt="">
          {{ $t('company') }}
        </div>
        <VTextField
          id="organization"
          v-model="data.company.name"
          outlined
          dense
          autocomplete="organization"
          :error-messages="errors['company.name']"
          @input="errors['company.name'] = null"
        >
          <template v-slot:label>
            {{ $t('companyName') }} *
          </template>
        </VTextField>
      </VCol>
    </VRow>

    <VRow>
      <VCol cols="12" class="py-0">
        <VTextField
          id="url"
          v-model="data.company.site"
          outlined
          dense
          :label="$t('companySite')"
          autocomplete="url"
          type="url"
          :error-messages="errors['company.site']"
          @input="errors['company.site'] = null"
        />
      </VCol>
    </VRow>

    <VRow>
      <VCol cols="12" class="py-0">
        <VAutocomplete
          :value="data.company.countryId"
          @input="onCountryChange"
          dense
          outlined
          :items="countries"
          item-value="id"
          item-text="name"
          :no-data-text="$t('noCountryFound')"
          autocomplete="disable-city"
          :error-messages="errors['company.countryId']"
          @change="errors['company.countryId'] = null"
        >
          <template v-slot:label>
            {{ $t('companyCountry') }} *
          </template>
        </VAutocomplete>
      </VCol>
    </VRow>

    <div>
      <div class="title title-register mb-md-8 mb-2 mt-5">
        <img src="../assets/img/rabota/int-login.svg" alt="">
        {{ $t('user') }}
      </div>
      <VTextField
        id="firstName"
        v-model="data.user.firstName"
        outlined
        dense
        autocomplete="given-name"
        :error-messages="errors['user.firstName']"
        @input="errors['user.firstName'] = null"
      >
        <template v-slot:label>
          {{ $t('firstName') }} *
        </template>
      </VTextField>
      <VTextField
        id="lastName"
        v-model="data.user.lastName"
        outlined
        dense
        autocomplete="family-name"
        :error-messages="errors['user.lastName']"
        @input="errors['user.lastName'] = null"
      >
        <template v-slot:label>
          {{ $t('lastName') }} *
        </template>
      </VTextField>
      <VTextField
        id="email"
        v-model="data.user.email"
        type="email"
        outlined
        dense
        autocomplete="email"
        :error-messages="errors['user.email']"
        @input="errors['user.email'] = null"
      >
        <template v-slot:label>
          {{ $t('email') }} *
        </template>
      </VTextField>
      <PhoneNumberInput
        v-model="data.user.phone"
        :key="country ? country.alpha2Code: 'RU'"
        :default-country="country ? country.alpha2Code: null"
        :auto-default-country="false"
        required
        :error-messages="errors['user.phone']"
        @input="errors['user.phone'] = null"
      />
      <VTextField
        v-model="data.user.password"
        class="mt-n2"
        outlined
        dense
        type="password"
        autocomplete="new-password"
        :error-messages="errors['user.password']"
        @input="errors['user.password'] = null"
      >
        <template v-slot:label>
          {{ $t('pass') }} *
        </template>
      </VTextField>
      <VTextField
        v-model="data.user.passwordConfirmation"
        outlined
        dense
        type="password"
        autocomplete="new-password"
        :error-messages="errors['user.passwordConfirmation']"
        @input="errors['user.passwordConfirmation'] = null"
      >
        <template v-slot:label>
          {{ $t('repeatPass') }} *
        </template>
      </VTextField>
    </div>

    <VRow no-gutters class="flex-column" align="center">
      <VCheckbox
        v-model="accepted"
        hide-details
        row
        class="mt-6 justify-center"
      >
        <template v-slot:label>
          <div class="body-2">
            {{ $t('agreeTo') }}
            <a :href="confidentialUrl" target="_blank" @click.stop>{{ $t('privacyPolicy') }}</a>
          </div>
        </template>
      </VCheckbox>
      <div v-if="hasErrorMessageOnly" class="mt-5 red--text text-center">
        {{ errorMessage }}
      </div>
      <VBtn
        color="success"
        elevation="0"
        class="my-md-12 my-6 xs-auto"
        type="submit"
        :disabled="!accepted"
        :loading="loading"
      >
        {{ $t('register') }}
      </VBtn>
    </VRow>
  </VForm>
</template>

<script>
import CryptoJS from 'crypto-js';
import PhoneNumberInput from '@/components/Input/PhoneNumberInput';
import useValidation from '@/compositions/useValidation';
import useRecaptcha from '@/compositions/useRecaptcha';
import {
  computed, reactive, ref, watch,
} from '@vue/composition-api';
import { getUtm, normalizePhoneNumber } from '@/functions';
import { response } from '@/orm/functions';
import Country from '@/orm/models/Country/Country';
import User from '@/orm/models/User/User';
import Company from '@/orm/models/Company/Company';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'RegistrationForm',
  components: {
    PhoneNumberInput,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    action: {
      type: Function,
      required: true,
    },
  },
  setup(props, { emit }) {
    const {
      withValidationHandling,
      errors,
      errorMessage,
      hasErrors,
      hasErrorMessageOnly,
      loading,
    } = useValidation();
    const { getRecaptchaToken } = useRecaptcha();
    const { locale } = useI18n();

    const data = ref({
      company: {
        name: null,
        site: null,
        countryId: null,
        ...props.company,
      },
      user: {
        firstName: null,
        lastName: null,
        email: null,
        phone: null,
        password: null,
        passwordConfirmation: null,
        ...props.user,
      },
    });
    const countries = reactive([]);
    const accepted = ref(false);

    const country = ref(null);

    async function saveCredentials() {
      if (window.PasswordCredential) {
        await navigator.credentials.store(new PasswordCredential(
          { password: data.value.user.password, id: data.value.user.email },
        ));
      }
    }

    async function register() {
      await withValidationHandling(async () => {
        const recaptchaToken = await getRecaptchaToken();

        await props.action({
          company: {
            name: data.value.company.name,
            site: data.value.company.site,
            countryId: data.value.company.countryId,
            test: data.value.company.test,
            partnerCode: data.value.company.partnerCode,
            invitedBy: data.value.company.invitedBy,
          },
          user: {
            firstName: data.value.user.firstName,
            lastName: data.value.user.lastName,
            email: data.value.user.email,
            phone: normalizePhoneNumber(data.value.user.phone),
            password: data.value.user.password,
            passwordConfirmation: data.value.user.passwordConfirmation,
          },
          utm: getUtm(),
          recaptchaToken,
        });

        const sendRegistrationTrackEvent = !!window.location.href && (window.location.href.includes('huntica.works') || window.location.href.includes('huntica.io'));

        if (sendRegistrationTrackEvent) {
          if (window.fbq) {
            window.fbq('trackCustom', 'successful-registration', {
              phone: data.value?.user?.phone ? CryptoJS.SHA256(normalizePhoneNumber(data.value.user.phone)).toString(CryptoJS.enc.Hex) : null,
              email: CryptoJS.SHA256(data.value?.user?.email).toString(CryptoJS.enc.Hex),
              firstName: CryptoJS.SHA256(data.value?.user?.firstName).toString(CryptoJS.enc.Hex),
              lastName: CryptoJS.SHA256(data.value?.user?.lastName).toString(CryptoJS.enc.Hex),
              companyName: data.value?.company?.name,
            });
          }

          if (window.twq) {
            window.twq('event', 'tw-onfdy-oodqi', {
              status: 'completed',
              // генерируем уникальный идентификатор для твиттера,
              conversion_id: window.crypto.randomUUID() + Date.now(),
              email_address: data.value?.user?.email,
              phone_number: data.value?.user?.phone ? normalizePhoneNumber(data.value.user.phone) : null,
            });
          }

          if (window.gtag) {
            window.gtag('event', 'conversion', {
              send_to: 'AW-11350477303/1c8ZCJjRk-YZEPeTqqQq',
            });
          }
        }

        await saveCredentials();
        await User.api().fetch();
        await Company.api().fetch();
      });
      if (!hasErrors.value) {
        emit('registered');
      }
    }

    function onCountryChange(val) {
      data.value.company.countryId = val;
      country.value = countries.find((c) => c.id === val);
    }

    async function loadCountries() {
      await withValidationHandling(async () => {
        Promise.all([
          countries.push(...await response(Country.api().fetch())),
        ]).then(() => {
          if (locale.value.toLowerCase() === 'en') {
            const usa = countries.find((countryUsa) => countryUsa.alpha3Code === 'USA');
            onCountryChange(usa.id);
          }
        });
      });
    }

    loadCountries();

    const confidentialUrl = computed(() => process.env.VUE_APP_CONFIDENTIAL_URL);

    return {
      errors,
      errorMessage,
      hasErrorMessageOnly,
      loading,
      countries,
      data,
      accepted,
      register,
      country,
      onCountryChange,
      confidentialUrl,
    };
  },
};
</script>

<style lang="scss" scoped>
  .title-register {
    display: flex;
    text-align: left;
    align-items: center;
    font-weight: bold;
    img {
      margin-right: 0.625rem;
    }
  }

</style>

<i18n locale="en" lang="yaml">
  companyName: Company name
  companySite: Company website
  user: User
  firstName: First name
  lastName: Last name
  email: E-mail
  pass: Password
  agreeTo: I agree to the
  privacyPolicy: privacy policy
  register: Register
  company: Company
  companyCountry: Country
  noCountryFound: Country not found
  repeatPass: Confirm password
</i18n>

<i18n locale="ru" lang="yaml">
  companyName: Название компании
  companySite: Сайт компании
  user: Пользователь
  firstName: Имя
  lastName: Фамилия
  email: E-mail
  pass: Пароль
  agreeTo: Согласен с
  privacyPolicy: политикой конфиденциальности
  register: Зарегистрироваться
  company: Компания
  companyCountry: Страна
  noCountryFound: Страна не найдена
  repeatPass: Повторите пароль
</i18n>
